import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RelationshipType } from 'src/components/atomsLegacy';
import { SubmissionTypesList } from 'src/components/pages/SubmissionTypes/SubmissionTypes.styles';
import { RELATIONSHIP_TYPES } from '../../../api';
import axios from '../../../axios';
import { selectRelationshipType } from '../../../creators/OpenReporting.creators';
import * as Types from '../../../types';
import { CategorisedType } from '../../../types';
import { Footer } from '../../moleculesLegacy/Footer';
import CustomHeader from '../../public/CustomHeader';
import ProgressBar from '../../public/ProgressBar';
import ResponsiveContainer from '../../public/ResponsiveContainer';
import {
  CustomHeaderContainer,
  PublicReportingContainer,
} from '../../public/questions/QuestionContainer/QuestionContainer.styles';

interface Props {
  isCountryEnabled?: boolean;
  isSiteEnabled?: boolean;
}

export const RelationshipTypes = ({
  isCountryEnabled = false,
  isSiteEnabled = false,
}: Props) => {
  const { t } = useTranslation();
  const [publicRelationshipTypes, setPublicRelationshipTypes] = useState<
    CategorisedType[]
  >([]);
  const [selected, setSelected] = useState<CategorisedType | null>(null);
  const organisation =
    useSelector(
      (state: Types.State) =>
        state.openReporting?.openReportReducer?.organisation,
    ) ?? '';
  const relationshipType = useSelector(
    (state: Types.State) =>
      state.openReporting?.openReportReducer?.relationshipToCompany,
  );
  const dispatch = useDispatch();

  const sanitizeRelationshipTypes = (
    relationshipTypes: Array<CategorisedType | null> | null | undefined,
  ): CategorisedType[] =>
    (relationshipTypes ?? []).filter(
      (type: CategorisedType | null) =>
        type?.id !== undefined &&
        typeof type?.name === 'string' &&
        type.name.length > 0,
    ) as CategorisedType[];

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      const response = await axios.get(RELATIONSHIP_TYPES(organisation), {
        signal: controller.signal,
      });
      const {
        data: { data },
      } = response;
      const sanitizedRelationshipTypes = sanitizeRelationshipTypes(data);
      setPublicRelationshipTypes(sanitizedRelationshipTypes);
    };
    fetchData();
    return () => {
      controller.abort();
    };
  }, [organisation]);

  useEffect(() => {
    const controller = new AbortController();
    const selectedType =
      publicRelationshipTypes.find(
        (type: CategorisedType) => type.id === relationshipType,
      ) || null;
    setSelected(selectedType);
    return () => {
      controller.abort();
    };
  }, [publicRelationshipTypes, relationshipType]);

  return (
    <PublicReportingContainer>
      <CustomHeaderContainer>
        <CustomHeader organisation={organisation} />
      </CustomHeaderContainer>

      <ResponsiveContainer
        includeLeftCircleButton={true}
        includeRightCircleButton={true}
        includeBottomButton={true}
        backLinkTo={`/${organisation}/open-reporting`}
        nextLinkTo={`/${organisation}/open-reporting/${
          isCountryEnabled
            ? 'country'
            : isSiteEnabled
              ? 'site'
              : 'submission-type'
        }`}
        nextLinkDisabled={!selected}
        buttonText={t('Buttons.next')}
        buttonStyles={{ textTransform: 'uppercase' }}
        nextButtonOnClick={() => {
          if (selected) dispatch(selectRelationshipType(selected));
        }}
      >
        <div className="public-reporting-header">
          <h2 className="public-reporting-heading">
            {t('public.Relationship.header')}
          </h2>
          <ProgressBar percentage={10} />
          <h3 className="public-reporting-subheading">
            {t('public.Relationship.tellUsAbout')}
          </h3>
        </div>

        <SubmissionTypesList data-testid="relationship-types-list">
          {publicRelationshipTypes.map((relationshipType, index) => (
            <RelationshipType
              index={index}
              active={
                selected !== null &&
                (selected.id === undefined
                  ? selected.name === relationshipType.name
                  : selected.id === relationshipType.id)
              }
              key={relationshipType.id?.toString() ?? index.toString()}
              onClick={() => setSelected(relationshipType)}
              name={relationshipType.name}
            />
          ))}
        </SubmissionTypesList>
      </ResponsiveContainer>

      <Footer id="desktop-powered-by-vault" />
    </PublicReportingContainer>
  );
};
